import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getAiShotImage,
  getDisplayableImageUrl,
  isRawFormat,
} from "@beleef/editor-shot";
import { FiFolderPlus } from "react-icons/fi";
import { FaArrowLeft } from "react-icons/fa";
import Button from "components/ui/Button";
import Modal from "components/ui/Modal";
import HelpGuide from "components/ui/HelpGuide";
import ImageContainer from "components/upload/ImageContainer";
import Lightbox from "components/ui/Lightbox";
import DownloadProgressToast from "components/ui/DownloadProgressToast";
import { useDownloadManager } from "hooks/useDownloadManager";
import useRawImageRenderer, {
  applyEditorReviewRevisionUrls,
} from "hooks/useRawImageRenderer";
import { showToast } from "components/ui/toast";
import { formatDateTime } from "utils/helperFunctions";
import {
  buildRevisionRequestItems,
  formatStatusLabel,
  getUploadKey,
  isEditorReviewComplete,
  isExtrasRawBatch,
  resolveImageRevisionUrl,
} from "utils/prepareMarketing";
import { useAddRawPhotoRevisionMutation } from "apis/prepareMarketing";
import useEditorPrintUpload from "./useEditorPrintUpload";
import CaptureRow from "./CaptureRow";
import BulkPrintDropzone from "./BulkPrintDropzone";
import WebCropModal from "./WebCropModal";
import ShotBoard from "./ShotBoard";
import "./editorUpload.css";

const downloadFolderName = (kind, address, propertyId, summary) =>
  `${kind}-${address || propertyId}-${summary || ""}`;

const toLightboxImages = (images = []) =>
  images
    .map((img) => {
      const url = getDisplayableImageUrl(img) || img?.url;
      if (!url || isRawFormat(url)) return null;
      return {
        ...img,
        url,
        name: img?.name || "",
      };
    })
    .filter(Boolean);

const processImagesWithRevisions = (images) => {
  if (!images) return [];
  return images.map((image) => {
    let imageUrl = image.url;
    let originalUrl = image.url;
    const isRaw = isRawFormat(image.name);
    if (image.revisions && image.revisions.length > 0) {
      const originalRev = image.revisions.find((r) => r.isOriginal === true);
      const activeRev = image.activeRevision
        ? image.revisions.find(
            (r) => String(r._id) === String(image.activeRevision),
          )
        : null;

      if (originalRev) originalUrl = originalRev.url;
      else if (!originalUrl) originalUrl = image.revisions[0].url;

      if (isRaw) {
        if (activeRev) imageUrl = activeRev.url;
        else if (originalRev) imageUrl = originalRev.url;
        else imageUrl = image.revisions[0].url;
      } else if (activeRev) {
        imageUrl = activeRev.url;
      } else if (originalRev) {
        imageUrl = originalRev.url;
      } else if (!imageUrl) {
        imageUrl = image.revisions[image.revisions.length - 1].url;
      }
    }
    return {
      ...image,
      url: imageUrl || originalUrl || image.url,
      originalUrl: originalUrl || imageUrl || image.url,
    };
  });
};

const getStatusStyle = (status) => {
  const styles = {
    overdue: "bg-red-50 text-red-600",
    revisionSent: "bg-red-50 text-red-600",
    pending: "bg-amber-50 text-amber-600",
    inProgress: "bg-lime-50 text-lime-600",
    revisionInProgress: "bg-lime-50 text-lime-600",
    editorUploaded: "bg-green-50 text-green-700",
    edited: "bg-blue-50 text-blue-600",
    revisionCompleted: "bg-blue-50 text-blue-600",
    review: "bg-violet-50 text-violet-600",
    completed: "bg-green-50 text-green-700",
  };
  return styles[status] || "bg-gray-50 text-gray-600";
};

/**
 * Editor upload card — print-only bulk upload with auto web generation.
 */
export default function EditorUploadCard({
  item,
  address,
  propertyId,
  editedWebPhotos = [],
  editedPrintPhotos = [],
  hdrMerges = [],
  editorShots = [],
  existingCount = 0,
  maxPhotoId = 0,
  allocatePhotoId,
  extraPhotos = [],
  commentedRawPhotos = [],
  api,
  headerExtra = null,
  banner = null,
  onReviewSuccess,
  onRefresh,
}) {
  const isExtrasBatch = isExtrasRawBatch(item);
  const showAsSelectedImages =
    isExtrasBatch || (extraPhotos?.length ?? 0) > 0;

  const [rawFolder, setRawFolder] = useState(false);
  const [lightbox, setLightbox] = useState({ images: [], index: -1 });
  const [addressedUploads, setAddressedUploads] = useState({});
  const [revisionUploadingKey, setRevisionUploadingKey] = useState(null);
  const [submitConfirm, setSubmitConfirm] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const { mutateAsync: addRawPhotoRevision } = useAddRawPhotoRevisionMutation();
  const rowInputRefs = useRef({});
  const printMasterLockRef = useRef(new Set());

  const { downloads, queueDownload, removeDownload } = useDownloadManager();

  const openLightbox = (images, index = 0) => {
    if (!images?.length || index < 0) return;
    setLightbox({ images, index });
  };
  const closeLightbox = () => setLightbox({ images: [], index: -1 });

  const [rawImages, setRawImages] = useState(() => {
    const processed = processImagesWithRevisions(item?.images);
    if (isExtrasBatch && commentedRawPhotos?.length > 0) {
      return applyEditorReviewRevisionUrls(processed, commentedRawPhotos);
    }
    return processed;
  });

  useEffect(() => {
    const processed = processImagesWithRevisions(item?.images);
    const next =
      isExtrasBatch && commentedRawPhotos?.length > 0
        ? applyEditorReviewRevisionUrls(processed, commentedRawPhotos)
        : processed;

    // Keep already-converted JPG/data URLs across marketing refetches so HDR
    // thumbnails don't go blank and re-queue dozens of RAW conversions.
    setRawImages((prev) => {
      const prevById = new Map(prev.map((img) => [String(img.id), img]));
      return next.map((img) => {
        const existing = prevById.get(String(img.id));
        if (!existing) return img;
        const existingDisplay = getDisplayableImageUrl(existing);
        const nextDisplay = getDisplayableImageUrl(img);
        if (
          existingDisplay &&
          !isRawFormat(existingDisplay) &&
          (!nextDisplay || isRawFormat(nextDisplay))
        ) {
          return {
            ...img,
            url: existing.url,
            originalUrl:
              existing.originalUrl || img.originalUrl || img.url,
            conversionFailed: existing.conversionFailed,
          };
        }
        return img;
      });
    });
  }, [item?.eventId, item?.images, item?.status, commentedRawPhotos, isExtrasBatch]);

  // Convert RAW → JPG for HDR thumbnails and capture-row previews, not only
  // when the raw folder grid is open.
  useRawImageRenderer({
    images: rawImages,
    setImages: setRawImages,
    enabled: true,
  });

  const upload = useEditorPrintUpload({
    item,
    propertyId,
    existingCount,
    editedWebPhotos,
    editedPrintPhotos,
    rawImages,
    editorShots,
    commentedRawPhotos,
    maxPhotoId,
    allocatePhotoId,
    api,
    onReviewSuccess,
  });

  const {
    rows,
    printCount,
    webCount,
    total,
    editCount,
    cropIndex,
    setCropIndex,
    isSending,
    isProcessingBatch,
    canSend,
    handleBulkFiles,
    handleFilesForRow,
    saveCrop,
    sendToReview,
  } = upload;

  const revisionRequestItems = useMemo(
    () =>
      buildRevisionRequestItems(
        commentedRawPhotos,
        rawImages,
        item?.images,
        addressedUploads,
      ),
    [commentedRawPhotos, rawImages, item?.images, addressedUploads],
  );

  const revisionRequestedImageIds = useMemo(
    () => new Set(revisionRequestItems.map((request) => request.imageId)),
    [revisionRequestItems],
  );

  const revisionRequestByImageId = useMemo(() => {
    const map = new Map();
    revisionRequestItems.forEach((request) => {
      map.set(request.imageId, request);
    });
    return map;
  }, [revisionRequestItems]);

  const inlineRevisionInSelected =
    isExtrasBatch && revisionRequestItems.length > 0;

  const rawImagesForFolder = useMemo(
    () =>
      inlineRevisionInSelected
        ? rawImages
        : rawImages.filter((img) => !revisionRequestedImageIds.has(img.id)),
    [rawImages, revisionRequestedImageIds, inlineRevisionInSelected],
  );

  const editorReviewComplete = useMemo(
    () =>
      isEditorReviewComplete(
        commentedRawPhotos,
        rawImages,
        item?.images,
        addressedUploads,
      ),
    [commentedRawPhotos, rawImages, item?.images, addressedUploads],
  );

  const displayStatus =
    editorReviewComplete && item?.status === "revisionInProgress"
      ? "editorUploaded"
      : item?.status;
  const displayStatusLabel =
    displayStatus === "editorUploaded"
      ? "Editor uploaded"
      : formatStatusLabel(displayStatus);

  useEffect(() => {
    if (inlineRevisionInSelected) setRawFolder(true);
  }, [inlineRevisionInSelected]);

  const handleRawRevisionUpload = async (
    event,
    imageId,
    requestedRevisionId,
    commentId,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !requestedRevisionId || commentId == null) return;
    const uploadKey = getUploadKey(imageId, commentId);
    try {
      setRevisionUploadingKey(String(imageId));
      const resp = await addRawPhotoRevision({
        propertyId,
        eventId: item?.eventId,
        imageId,
        commentId,
        file,
      });
      if (resp?.success) {
        const updatedImage = resp.data.image;
        const uploadedRevisionId = resp.data.revisionId;
        const uploadedUrl =
          updatedImage.revisions?.find(
            (r) => String(r._id) === String(uploadedRevisionId),
          )?.url || updatedImage.url;

        setAddressedUploads((prev) => ({
          ...prev,
          [uploadKey]: { uploadedRevisionId, url: uploadedUrl },
        }));
        setRawImages((prev) =>
          prev.map((img) => {
            if (img.id !== imageId) return img;
            const merged = {
              ...img,
              revisions: updatedImage.revisions,
              activeRevision: updatedImage.activeRevision,
            };
            const requestedUrl = resolveImageRevisionUrl(
              merged,
              requestedRevisionId,
            );
            return {
              ...merged,
              url: requestedUrl || img.url,
            };
          }),
        );
        showToast("success", "Revision uploaded");
        onRefresh?.();
      }
    } catch (error) {
      console.error("Error uploading rawPhoto revision:", error);
      showToast(
        "error",
        error?.response?.data?.message ||
          error.message ||
          "Failed to upload revision",
      );
    } finally {
      setRevisionUploadingKey(null);
    }
  };

  const renderRevisionCard = (request) => {
    const isRevisionUploading =
      revisionUploadingKey === request.key ||
      revisionUploadingKey === `${request.imageId}-${request.commentId}`;
    return (
      <div
        key={request.key}
        className={`flex flex-col gap-1.5 rounded-md p-1 ${
          request.addressed
            ? "bg-green-50 border border-green-200"
            : "bg-red-50 border border-red-200"
        }`}
      >
        <ImageContainer
          image={{
            ...request.image,
            url:
              getDisplayableImageUrl({
                ...request.image,
                url: request.requestedUrl || request.image.url,
              }) ||
              request.requestedUrl ||
              request.image.url,
            comments: request.comments,
          }}
          viewMode="grid"
          showDelete={false}
          showDownload
          eventId={item?.eventId}
          propertyId={propertyId}
          onPreview={() => {
            const url =
              getDisplayableImageUrl({
                ...request.image,
                url: request.requestedUrl || request.image.url,
              }) || request.requestedUrl;
            if (!url || isRawFormat(url)) return;
            openLightbox(
              [
                {
                  ...request.image,
                  url,
                  name: request.image?.name || "Revision",
                },
              ],
              0,
            );
          }}
        />
        {!request.addressed && request.pendingCount > 1 && (
          <p className="text-[10px] text-red-700 font-medium m-0 text-center">
            {request.pendingCount} revision notes pending
          </p>
        )}
        {request.addressed ? (
          <div className="flex flex-col gap-1">
            {request.uploadedUrl &&
              request.uploadedUrl !== request.requestedUrl && (
                <div className="relative rounded overflow-hidden border border-green-200">
                  <img
                    src={request.uploadedUrl}
                    alt="Uploaded revision"
                    className="w-full h-16 object-cover"
                  />
                  <span className="absolute bottom-0.5 left-0.5 text-[9px] bg-black/60 text-white px-1 rounded">
                    Your upload
                  </span>
                </div>
              )}
          </div>
        ) : (
          <label
            className={`flex items-center justify-center text-[11px] font-semibold rounded cursor-pointer px-2 py-1 border ${
              isRevisionUploading
                ? "bg-lightergray text-darkergray border-mediumgray cursor-not-allowed"
                : "bg-black text-white border-black hover:opacity-90"
            }`}
          >
            <input
              type="file"
              accept="image/*"
              className="hidden"
              disabled={isRevisionUploading}
              onChange={(e) =>
                handleRawRevisionUpload(
                  e,
                  request.imageId,
                  request.revisionId,
                  request.commentId,
                )
              }
            />
            {isRevisionUploading ? "Uploading..." : "Upload Revision"}
          </label>
        )}
      </div>
    );
  };

  const renderSelectedFolderImage = (image, index) => {
    const request = revisionRequestByImageId.get(image.id);
    if (!request) {
      return (
        <ImageContainer
          key={image.id}
          image={{
            ...image,
            url: getDisplayableImageUrl(image) || image.url,
          }}
          viewMode="grid"
          showDownload
          showDelete={false}
          onPreview={() => {
            const items = toLightboxImages(rawImagesForFolder);
            const clickedUrl = getDisplayableImageUrl(image) || image.url;
            const lightboxIndex = items.findIndex(
              (img) =>
                String(img.id) === String(image.id) || img.url === clickedUrl,
            );
            openLightbox(
              items,
              lightboxIndex >= 0 ? lightboxIndex : index,
            );
          }}
        />
      );
    }
    return renderRevisionCard(request);
  };

  const handleUseAsPrintMaster = async (shot, imageUrl) => {
    const ai = getAiShotImage(shot);
    if (!ai?.url) {
      showToast(
        "error",
        "Run AI edit before using as print master — HDR alone cannot be used",
      );
      return;
    }
    const printUrl = ai.url;
    if (imageUrl && imageUrl !== printUrl) {
      showToast(
        "error",
        "Only the AI-edited image can be used as print master",
      );
      return;
    }
    const lockKey = String(shot?.shotId || printUrl);
    if (printMasterLockRef.current.has(lockKey)) return;
    printMasterLockRef.current.add(lockKey);
    try {
      const response = await fetch(printUrl);
      const blob = await response.blob();
      const file = new File(
        [blob],
        `${shot.label || shot.shotId || "shot"}.jpg`,
        { type: blob.type || "image/jpeg" },
      );
      // Prefer the capture row bound to this shot — never dump into slot 1.
      const shotRowIndex = rows.findIndex(
        (row) =>
          row.shotId != null && String(row.shotId) === String(shot.shotId),
      );
      const emptyIndex = rows.findIndex((row) => !row.print?.url);
      const targetIndex =
        shotRowIndex >= 0
          ? shotRowIndex
          : emptyIndex >= 0
            ? emptyIndex
            : 0;
      const targetRow = rows[targetIndex];
      const captureId = await handleFilesForRow(targetIndex, [file]);
      const printPhotoId = captureId ?? targetRow?.captureId;
      if (
        api?.updateEditorShot &&
        shot?.shotId &&
        printPhotoId != null &&
        item?.eventId
      ) {
        try {
          await api.updateEditorShot({
            propertyId,
            eventId: item.eventId,
            shotId: shot.shotId,
            printPhotoId,
          });
        } catch (linkError) {
          console.error("Failed to link printPhotoId to shot:", linkError);
        }
      }
      onRefresh?.();
      showToast("success", "Added as print master");
    } catch (error) {
      showToast(
        "error",
        error.message || "Failed to use image as print master",
      );
    } finally {
      printMasterLockRef.current.delete(lockKey);
    }
  };

  const pct = total ? Math.round((webCount / total) * 100) : 0;
  const cropRow = cropIndex != null ? rows[cropIndex] : null;

  const printDownloadImages = useMemo(
    () =>
      rows
        .filter((row) => row.print?.url)
        .map((row) => ({
          id: row.print.id || `print-${row.captureId}`,
          name: row.print.name || `print-${row.captureId}.jpg`,
          url: row.print.url,
        })),
    [rows],
  );

  const webDownloadImages = useMemo(
    () =>
      rows
        .filter((row) => row.web?.url)
        .map((row) => ({
          id: row.web.id || `web-${row.captureId}`,
          name: row.web.name || `web-${row.captureId}.jpg`,
          url: row.web.url,
        })),
    [rows],
  );

  const handleDownloadPrint = () => {
    if (!printDownloadImages.length) {
      showToast("error", "No print images to download");
      return;
    }
    queueDownload(
      printDownloadImages,
      downloadFolderName("print", address, propertyId, item?.summary),
    );
  };

  const handleDownloadWeb = () => {
    if (!webDownloadImages.length) {
      showToast("error", "No web images to download");
      return;
    }
    queueDownload(
      webDownloadImages,
      downloadFolderName("web", address, propertyId, item?.summary),
    );
  };

  const handleEditedPreview = (kind, rowIndex) => {
    if (kind === "source") {
      const items = rows
        .map((row) => {
          const url = getDisplayableImageUrl(row.rawImage);
          if (!url || isRawFormat(url)) return null;
          return {
            url,
            name: row.rawImage?.name || `source-${row.captureId}`,
            rowIndex: row.index,
          };
        })
        .filter(Boolean);
      const index = items.findIndex((img) => img.rowIndex === rowIndex);
      openLightbox(items, index);
      return;
    }

    if (kind === "print") {
      const items = rows
        .filter((row) => row.print?.url)
        .map((row) => ({
          url: row.print.url,
          name: row.print.name || `print-${row.captureId}`,
          rowIndex: row.index,
        }));
      const index = items.findIndex((img) => img.rowIndex === rowIndex);
      openLightbox(items, index);
      return;
    }

    if (kind === "web") {
      const items = rows
        .filter((row) => row.web?.url)
        .map((row) => ({
          url: row.web.url,
          name: row.web.name || `web-${row.captureId}`,
          rowIndex: row.index,
        }));
      const index = items.findIndex((img) => img.rowIndex === rowIndex);
      openLightbox(items, index);
    }
  };

  const summaryText = useMemo(() => {
    if (isProcessingBatch) return "Uploading print masters…";
    if (!printCount) return "No files uploaded yet.";
    if (webCount < printCount) return "Generating web versions…";
    if (webCount >= editCount) {
      return (
        <>
          <strong>
            {webCount}/{total}
          </strong>{" "}
          captures complete · web auto-generated.
        </>
      );
    }
    return "Upload print masters to continue.";
  }, [isProcessingBatch, printCount, webCount, editCount, total]);

  const shotsMissingPrintMaster = useMemo(() => {
    const arranged = (editorShots || []).filter((s) => s?.mainImageId != null);
    if (!arranged.length) {
      return Math.max(0, total - printCount);
    }
    return arranged.filter((s) => s.printPhotoId == null).length;
  }, [editorShots, total, printCount]);

  const confirmSendToReview = async () => {
    setSubmitConfirm(false);
    await sendToReview();
  };

  return (
    <div className="editor-upload-card w-full">
      {banner}

      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap pb-[18px] border-b border-mediumgray">
        <h1 className="text-[13px] font-bold uppercase tracking-[0.1em]">
          {item?.summary || "Photo Edit"}
        </h1>
        <span
          className={`text-[11px] font-semibold rounded-full px-2.5 py-0.5 ${getStatusStyle(
            displayStatus,
          )}`}
        >
          {displayStatusLabel}
        </span>
        {headerExtra}
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          className="ml-auto text-xs font-semibold text-darkergray hover:text-black border-0 bg-transparent cursor-pointer px-2 py-1"
        >
          How it works
        </button>
        {item?.createdAt && (
          <div className="w-full font-mono text-[12px] text-darkgray mt-1.5">
            {formatDateTime(item.createdAt)}
          </div>
        )}
      </div>

      {/* Raw images */}
      <div className="flex items-center justify-between gap-5 mt-7">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-darkergray">
          {showAsSelectedImages ? "Selected Images" : "Raw Images"}
        </h2>
        <button
          type="button"
          className="bg-black text-white font-semibold text-[13px] px-[22px] py-2.5 rounded-lg hover:opacity-80 transition-opacity"
          onClick={() =>
            queueDownload(
              rawImagesForFolder
                .map((img) => {
                  const request = revisionRequestByImageId.get(img.id);
                  return {
                    ...img,
                    url:
                      request?.requestedUrl ||
                      img.originalUrl ||
                      img.url,
                  };
                })
                .filter((img) => !!img.url),
              `${showAsSelectedImages ? "selected" : "raw"}-${address || propertyId}-${item?.summary || ""}`,
            )
          }
        >
          Download
        </button>
      </div>
      {item?.notes && (
        <p className="mt-3.5 text-[13px] text-darkergray max-w-[880px] leading-relaxed">
          <strong>Instructions:</strong> {item.notes}
        </p>
      )}

      {!rawFolder ? (
        <div
          className="w-[120px] h-[104px] border border-mediumgray rounded-[10px] flex flex-col items-center justify-center gap-2.5 bg-lightgray mt-3.5 cursor-pointer hover:border-mediumgray transition-colors"
          role="button"
          tabIndex={0}
          onClick={() => setRawFolder(true)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setRawFolder(true);
          }}
        >
          <FiFolderPlus className="w-7 h-7 text-darkgray" />
          <span className="font-bold text-[12px] uppercase tracking-wide">
            {showAsSelectedImages ? "Selected" : "Raw"}
          </span>
        </div>
      ) : (
        <div className="flex flex-col gap-4 mt-3.5">
          <Button
            onClick={() => setRawFolder(false)}
            className="gray-button flex items-center mr-auto"
          >
            <FaArrowLeft className="mr-1.5" /> Back
          </Button>
          <div className="grid gap-3 mb-4 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {rawImagesForFolder.map((image, index) =>
              inlineRevisionInSelected
                ? renderSelectedFolderImage(image, index)
                : (
                  <ImageContainer
                    key={image.id}
                    image={{
                      ...image,
                      url: getDisplayableImageUrl(image) || image.url,
                    }}
                    viewMode="grid"
                    showDownload
                    showDelete={false}
                    onPreview={() => {
                      const items = toLightboxImages(rawImagesForFolder);
                      const clickedUrl =
                        getDisplayableImageUrl(image) || image.url;
                      const lightboxIndex = items.findIndex(
                        (img) =>
                          String(img.id) === String(image.id) ||
                          img.url === clickedUrl,
                      );
                      openLightbox(
                        items,
                        lightboxIndex >= 0 ? lightboxIndex : index,
                      );
                    }}
                  />
                ),
            )}
          </div>
        </div>
      )}

      {revisionRequestItems.length > 0 && !inlineRevisionInSelected && (
        <div className="mt-7">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-darkergray mb-3.5">
            Revision Requested
          </h2>
          <div className="grid gap-3 grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {revisionRequestItems.map((request) => renderRevisionCard(request))}
          </div>
        </div>
      )}

      <ShotBoard
        images={rawImages}
        propertyId={propertyId}
        eventId={item?.eventId}
        api={api}
        existingShots={editorShots}
        onRefresh={onRefresh}
        onPreview={openLightbox}
        onUseAsPrintMaster={handleUseAsPrintMaster}
      />

      {/* Edited images */}
      <div className="flex items-center justify-between gap-5 mt-8 flex-wrap">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.1em] text-darkergray">
          Edited Images
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            disabled={!printDownloadImages.length}
            onClick={handleDownloadPrint}
            className="bg-black text-white font-semibold text-[13px] px-[18px] py-2.5 rounded-lg hover:opacity-80 transition-opacity disabled:bg-mediumgray disabled:cursor-not-allowed"
          >
            Download Print
          </button>
          <button
            type="button"
            disabled={!webDownloadImages.length}
            onClick={handleDownloadWeb}
            className="bg-black text-white font-semibold text-[13px] px-[18px] py-2.5 rounded-lg hover:opacity-80 transition-opacity disabled:bg-mediumgray disabled:cursor-not-allowed"
          >
            Download Web
          </button>
        </div>
      </div>

      {/* Status strip */}
      <div
        className="flex items-center gap-5 flex-wrap mt-5 px-[18px] py-3.5 bg-lightgray rounded-[10px]"
        role="status"
      >
        <div className="flex items-center gap-2 text-[13px]">
          <span className="w-2 h-2 rounded-[2px] bg-black flex-none" />
          <span>Print masters</span>
          <strong className="font-mono text-[12px]">{printCount}</strong>
          <span>/</span>
          <strong className="font-mono text-[12px]">{total}</strong>
        </div>
        <div className="flex items-center gap-2 text-[13px]">
          <span className="w-2 h-2 rounded-[2px] bg-mediumgray flex-none" />
          <span>Web (auto)</span>
          <strong className="font-mono text-[12px]">{webCount}</strong>
          <span>/</span>
          <strong className="font-mono text-[12px]">{total}</strong>
        </div>
        <div className="flex-1 min-w-[160px] h-[5px] bg-mediumgray rounded-full overflow-hidden">
          <div
            className="h-full bg-black rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <BulkPrintDropzone
        uploaded={printCount > 0}
        disabled={isProcessingBatch || isSending}
        processing={isProcessingBatch}
        onFiles={handleBulkFiles}
        printCount={printCount}
        total={total}
      />

      <div
        className="editor-capture-headers grid gap-3.5 px-4 pt-[22px] pb-2 font-mono text-[10px] uppercase tracking-[0.1em] text-darkgray font-bold"
        style={{ gridTemplateColumns: "36px 1fr 1fr 1.5fr 148px" }}
      >
        <span>#</span>
        <span>Source capture</span>
        <span>Print master</span>
        <span>Web (auto · 1600px)</span>
        <span>Status</span>
      </div>

      <div className="flex flex-col gap-2" role="list">
        {rows.map((row) => (
          <CaptureRow
            key={row.captureId}
            row={row}
            propertyId={propertyId}
            eventId={item?.eventId}
            fileInputRef={(el) => {
              rowInputRefs.current[row.index] = el;
            }}
            onOpenCrop={setCropIndex}
            onPreview={handleEditedPreview}
            onUploadClick={(index, files) => {
              if (files) {
                handleFilesForRow(index, files);
              } else if (rowInputRefs.current[index]) {
                rowInputRefs.current[index].click();
              }
            }}
          />
        ))}
      </div>

      <div className="flex justify-between items-center mt-6 gap-3.5 flex-wrap">
        <div className="text-[13px] text-darkgray" role="status">
          {summaryText}
        </div>
        <button
          type="button"
          disabled={!canSend}
          onClick={() => setSubmitConfirm(true)}
          className="bg-black text-white font-semibold text-[13px] px-[22px] py-2.5 rounded-lg hover:opacity-80 transition-opacity disabled:bg-mediumgray disabled:cursor-not-allowed"
        >
          {isSending ? "Sending…" : "Send to Review"}
        </button>
      </div>

      <p className="mt-5 text-[11.5px] text-darkgray text-center leading-relaxed">
        Upload print masters only — web versions are generated automatically
        (1600px · compressed).
      </p>

      <WebCropModal
        open={cropIndex != null && !!cropRow}
        imageUrl={cropRow?.print?.url || cropRow?.web?.url}
        label={cropRow?.rawImage?.name || `capture ${cropRow?.captureId}`}
        initialRect={cropRow?.cropRect}
        onSave={(rect) => saveCrop(cropIndex, rect)}
        onClose={() => setCropIndex(null)}
      />

      <DownloadProgressToast downloads={downloads} onRemove={removeDownload} />
      <Lightbox
        images={lightbox.images}
        currentIndex={lightbox.index}
        isOpen={lightbox.index >= 0 && lightbox.images.length > 0}
        onClose={closeLightbox}
      />

      {helpOpen && (
        <HelpGuide mode="editor" onClose={() => setHelpOpen(false)} />
      )}

      <Modal
        isOpen={submitConfirm}
        onClose={() => setSubmitConfirm(false)}
        title="Submit job"
      >
        <div className="flex flex-col space-y-4">
          <p className="text-sm text-darkergray">
            Once submitted this cannot be undone. Make sure you&apos;re happy
            with all print masters before continuing.
          </p>
          <div className="border border-mediumgray rounded-xl p-3 text-xs space-y-2">
            <div className="flex justify-between">
              <span className="font-medium">Print masters ready</span>
              <span className="font-mono font-bold">
                {printCount} / {total || editCount}
              </span>
            </div>
            {shotsMissingPrintMaster > 0 && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-[12px] text-amber-900">
                  {shotsMissingPrintMaster} shot
                  {shotsMissingPrintMaster !== 1 ? "s" : ""} still without a
                  print master link — confirm before sending.
                </span>
              </div>
            )}
          </div>
          <div className="w-full flex gap-2 justify-end">
            <Button
              onClick={() => setSubmitConfirm(false)}
              className="gray-button"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmSendToReview}
              className="black-button"
              loading={isSending}
            >
              Submit job
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
